import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { Tooltip, message, Space, Button, Table, Popover } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { PiCheckBold, PiXBold } from 'react-icons/pi';
import CollectItemFilterPopup, { FilterOptions } from '../components/CollectItemFilterPopup/CollectItemFilterPopup';
import { convertFiltersToStrapiQuery } from '~/utils/strapiFilters';
import { ColumnsType, TableProps } from 'antd/es/table';
import MobileCardList from '../components/MobileCardList';
import { useScreenSize } from '~/hooks/useScreenSize';
import {
  CollectItemAPIs,
  CollectItem_GetSyncablePage_RequestQuery,
  CollectItem_GetSyncablePage_Response,
  CollectItem_GetListWithUUIDs_ResponseItem,
  CollectItem_Export_RequestQuery,
} from '~/common/typescript/apis/CollectItemAPIs';
import {
  CollectFormAPIs,
  CollectForm_GetSyncablePage_RequestQuery,
  CollectForm_GetSyncablePage_Response,
  CollectForm_GetListWithUUIDs_ResponseItem,
} from '~/common/typescript/apis/CollectFormAPIs';
import { StreamSaverUtils } from '~/utils/StreamSaverUtils';
import { API_URL_ADMIN } from '~/config/config';
import { CollectItem_Type } from '~/common/typescript/models/CollectItem';
import GeometryPreview from '../components/GeometryPreview';
import { zoomToFeature } from '../utils/MapHighlightUtils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { userProfileModalAtom } from '~/components/Map/components/UserProfile/UserProfileStore';
import { currentProjectAtom } from '~/stores/ProjectStore';
import {
  refreshCollectItemsAtom,
  collectItemPopupPropsAtom,
  geocollectItemPropertiesModeAtom,
} from '~/components/Map/controllers/popups/CollectItemPopup';
import { selectedFormUUIDAtom, availableFormsAtom, selectedFormDataAtom, isMaximizedAtom } from '../atoms/CollectFormAtoms';
import styled from 'styled-components';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import UUIDViewer from '~/components/common/UUIDViewer';
import CollectFormSelector from '~/components/app_common/CollectFormSelector/CollectFormSelector';
import BulkActionsBar from '../components/BulkActionsBar';
import FormListPanel from './CollectData/FormListPanel';
import Toolbar from './CollectData/Toolbar';
import ExportModal from './CollectData/ExportModal';
import { parseFormContent, findTitleField, getTextFieldsFromContent } from '../utils/CollectFormParser';
import FieldTypeIcon from '../utils/FieldTypeIcon';

interface CollectDataTabProps {
  collectFormUuid?: string;
  tableHeight?: string;
  onMaximize?: () => void;
  onClose?: () => void;
}

const CollectDataTab = ({
  collectFormUuid,
  tableHeight,
  onMaximize,
  onClose,
}: CollectDataTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);
  const refreshTrigger = useAtomValue(refreshCollectItemsAtom);
  const setCollectItemPopupProps = useSetAtom(collectItemPopupPropsAtom);
  const setGeocollectItemPropertiesMode = useSetAtom(geocollectItemPropertiesModeAtom);
  const [selectedFormUUID] = useAtom(selectedFormUUIDAtom);
  const [availableForms, setAvailableForms] = useAtom(availableFormsAtom);
  const selectedFormData = useAtomValue(selectedFormDataAtom);
  const isMaximized = useAtomValue(isMaximizedAtom);
  const { isMobile } = useScreenSize();
  const [collectItems, setCollectItems] = useState<CollectItem_GetListWithUUIDs_ResponseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<string | null>(null); // uuid of item being confirmed for deletion
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    pageSize: isMobile ? 15 : 10, // Increase page size for mobile
    pageCount: 0,
    total: 0,
  });

  // Filter states
  const [filterPopupVisible, setFilterPopupVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({});

  // Multiselect states
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Fetch available forms
  useEffect(() => {
    const fetchAvailableForms = async () => {
      if (!currentProject?.id) return;

      try {
        const response = await CollectFormAPIs.remote_CollectForm_getPage({
          projectId: currentProject.id,
          page: 1,
          pageSize: 100,
        });

        setAvailableForms(response.results);
      } catch (error) {
        console.error('Error fetching available forms:', error);
      }
    };

    fetchAvailableForms();
  }, [currentProject?.id]);

  const fetchCollectItems = useCallback(async () => {
    if (!selectedFormUUID) return;

    setLoading(true);
    try {
      // Convert UI filters to Strapi filters
      const strapiFilters = convertFiltersToStrapiQuery(appliedFilters);

      // First get syncable page to get UUIDs
      const syncableRequest: CollectItem_GetSyncablePage_RequestQuery = {
        collect_form_uuid: selectedFormUUID,
        page: paginationInfo.page,
        pageSize: paginationInfo.pageSize,
        no_deleted: true,
        filters: Object.keys(strapiFilters).length > 0 ? strapiFilters : undefined,
      };

      const syncableResponse: CollectItem_GetSyncablePage_Response =
        await CollectItemAPIs.remote_getPageSyncable(syncableRequest);

      if (syncableResponse.results.length > 0) {
        // Get detailed collect items with UUIDs
        const uuids = syncableResponse.results.map((item: { uuid: string }) => item.uuid);
        const detailedItems = await CollectItemAPIs.remote_getCollectItemsWithUUIDs({
          collect_form_uuid: selectedFormUUID,
          uuids: uuids,
        });

        setCollectItems(detailedItems);
      } else {
        setCollectItems([]);
      }

      setPaginationInfo({
        ...paginationInfo,
        total: syncableResponse.pagination.total,
        pageCount: syncableResponse.pagination.pageCount,
      });
    } catch (error) {
      console.error('Error fetching collect items:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFormUUID, paginationInfo.page, paginationInfo.pageSize, appliedFilters]);

  // Fetch items when selected form changes
  useEffect(() => {
    setPaginationInfo(prev => ({ ...prev, page: 1 }));
    fetchCollectItems();
  }, [selectedFormUUID]);

  // Since we're now using server-side filtering, we use collectItems directly
  const filteredCollectItems = collectItems;

  const highlightOnMap = (record: CollectItem_GetListWithUUIDs_ResponseItem) => {
    console.log('Zooming to feature on map:', record);

    if (record.geom) {
      try {
        const geometry = JSON.parse(record.geom);
        zoomToFeature(geometry);
      } catch (error) {
        console.error('Error parsing geometry:', error);
      }
    } else {
      console.warn('No geometry data available for this record');
    }
  };

  // Convert data for mobile card list
  const mobileCardData = filteredCollectItems.map((item) => {
    let lat: number | undefined;
    let lng: number | undefined;

    if (item.geom) {
      try {
        const geometry = JSON.parse(item.geom);

        if (geometry?.coordinates) {
          if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
            // Point: [lng, lat]
            const [longitude, latitude] = geometry.coordinates;
            if (typeof longitude === 'number' && typeof latitude === 'number') {
              lng = longitude;
              lat = latitude;
            }
          } else if (geometry.coordinates[0] && Array.isArray(geometry.coordinates[0])) {
            // Polygon/LineString: [[lng, lat], ...] or [[[lng, lat], ...]]
            const firstCoord = Array.isArray(geometry.coordinates[0][0])
              ? geometry.coordinates[0][0]
              : geometry.coordinates[0];
            if (firstCoord && typeof firstCoord[0] === 'number' && typeof firstCoord[1] === 'number') {
              lng = firstCoord[0];
              lat = firstCoord[1];
            }
          }
        }
      } catch (error) {
        console.error('Error parsing geometry coordinates:', error);
      }
    }

    // Get title from form content data
    let title = 'Dữ liệu thu thập';
    const titleField = findTitleField(selectedFormData);

    if (titleField) {
      const value = item.properties?.[titleField.key];
      if (value && value !== '') {
        title = String(value).substring(0, 50);
      } else {
        title = `${titleField.label} (trống)`;
      }
    }

    // Get form name from selectedFormData or availableForms
    const formName =
      selectedFormData?.name || availableForms.find((form) => form.uuid === item.collect_form_uuid)?.name || 'Không rõ';

    return {
      id: item.uuid || '',
      title: title,
      description: undefined, // Remove description to clean up card
      creator: item.user?.fullname || item.user?.username || 'Không rõ',
      creatorId: item.user?.id?.toString(),
      formName: formName,
      lat: lat,
      lng: lng,
      createdAt: item.client_created_at,
      syncAt: item.sync_at,
      geom: item.geom, // Add geometry data for preview
      properties: item.properties,
      user: item.user,
      uuid: item.uuid,
      collect_form_uuid: item.collect_form_uuid,
    };
  });

  // Edit and delete functionality - moved before useMemo
  const handleEditClick = useCallback(
    (item: CollectItem_GetListWithUUIDs_ResponseItem) => {
      // Open GeocollectItemPanel with the item data
      setCollectItemPopupProps({
        title: 'Sửa dữ liệu',
        properties: {
          uuid: item.uuid,
          collect_form_uuid: item.collect_form_uuid,
          ...item.properties,
        },
        lngLat: undefined, // We don't have coordinates from table click
      });

      // Set mode to EDIT
      setGeocollectItemPropertiesMode('EDIT');
    },
    [setCollectItemPopupProps, setGeocollectItemPropertiesMode],
  );

  const handleDelete = useCallback(
    async (uuid: string) => {
      try {
        await CollectItemAPIs.remote_delete(uuid);
        message.success('Đã xóa dữ liệu thành công');
        await fetchCollectItems(); // Refresh list
      } catch (error) {
        console.error('Error deleting collect item:', error);
        message.error('Lỗi khi xóa dữ liệu');
      }
    },
    [fetchCollectItems],
  );

  const handleDeleteConfirm = useCallback(
    (uuid: string) => {
      setDeleteConfirmVisible(null); // Hide confirm popover
      handleDelete(uuid);
    },
    [handleDelete],
  );

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmVisible(null);
  }, []);

  // Generate dynamic columns based on form content schema
  const columns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = useMemo(() => {
    const baseColumns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = [
      {
        title: 'UUID',
        dataIndex: 'uuid',
        key: 'uuid',
        width: 100,
        fixed: 'left',
        render: (uuid: string) => <UUIDViewer uuid={uuid} />,
      },
      {
        title: 'Hình học',
        dataIndex: 'geom',
        key: 'preview',
        width: 80,
        render: (geom: string) => {
          try {
            const geometry = geom ? JSON.parse(geom) : null;
            return geometry ? (
              <GeometryPreview geometry={geometry} width={60} height={40} />
            ) : (
              <span style={{ fontSize: '10px', color: '#ccc' }}>No geom</span>
            );
          } catch (error) {
            return <span style={{ fontSize: '10px', color: '#ccc' }}>Invalid</span>;
          }
        },
      },
    ];

    // Add dynamic columns from form content schema
    const dynamicColumns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = [];

    const parsedContent = parseFormContent(selectedFormData);
    if (parsedContent && parsedContent.fields.length > 0) {
      parsedContent.fields.forEach((field) => {
        dynamicColumns.push({
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <FieldTypeIcon type={field.type} fieldKey={field.key} />
              <span>{field.label || field.key}</span>
            </span>
          ),
          key: field.key,
          width: 130,
          render: (_, record: any) => {
            let value = record.properties?.[field.key];

            if (!value && value !== 0) return <span style={{ color: '#ccc' }}>-</span>;

            const fieldType = (field.type || '').toLowerCase();
            const isDateField =
              fieldType === 'date' ||
              fieldType === 'datetime' ||
              fieldType === 'time' ||
              field.key.toLowerCase().includes('date') ||
              field.key.toLowerCase().includes('time');

            if (isDateField) {
              const dateVal =
                typeof value === 'string' && !isNaN(Number(value)) && Number(value) > 1000000000000
                  ? Number(value)
                  : value;
              return (
                <span style={{ fontSize: '12px' }}>
                  <DateTimeViewer date={dateVal} initialMode="relative" />
                </span>
              );
            }

            if (typeof value === 'boolean' || fieldType === 'boolean') {
              return (
                <span style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {value ? (
                    <>
                      <PiCheckBold style={{ color: 'var(--color-success, #22c55e)' }} /> Có
                    </>
                  ) : (
                    <>
                      <PiXBold style={{ color: 'var(--color-danger, #ef4444)' }} /> Không
                    </>
                  )}
                </span>
              );
            }

            if (typeof value === 'number') {
              return <span style={{ fontSize: '12px' }}>{value.toLocaleString('vi-VN')}</span>;
            }

            return <span style={{ fontSize: '12px' }}>{String(value)}</span>;
          },
        });
      });
    }

    // Add fixed columns at the end
    const endColumns: ColumnsType<CollectItem_GetListWithUUIDs_ResponseItem> = [
      {
        title: 'Ngày đồng bộ',
        dataIndex: 'sync_at',
        key: 'sync_at',
        width: 120,
        render: (syncAt: string) => (
          <span style={{ fontSize: '12px' }}>
            {syncAt ? <DateTimeViewer date={syncAt} initialMode='relative' /> : <span style={{ color: '#ccc' }}>-</span>}
          </span>
        ),
      },
      {
        title: 'Người tạo',
        dataIndex: 'user',
        key: 'user',
        width: 120,
        fixed: 'right',
        render: (user: any) => (
          <CreatorName
            onClick={(e) => {
              e.stopPropagation();
              if (user?.id) {
                setUserProfileModal({
                  visible: true,
                  userId: user.id,
                });
              }
            }}
          >
            {user?.fullname || user?.username || 'Không rõ'}
          </CreatorName>
        ),
      },
      {
        title: 'Hành động',
        key: 'action',
        width: 100,
        fixed: 'right',
        render: (_, record: CollectItem_GetListWithUUIDs_ResponseItem) => (
          <Space size="middle">
            <Tooltip title="Sửa">
              <Button
                disabled={false}
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(record);
                }}
              />
            </Tooltip>
            <Popover
              content={
                <div style={{ width: 180 }}>
                  <div style={{ marginBottom: 8 }}>Bạn có chắc chắn muốn xóa?</div>
                  <Space>
                    <Button size="small" onClick={() => handleDeleteConfirm(record.uuid)} danger>
                      Xóa
                    </Button>
                    <Button size="small" onClick={handleDeleteCancel}>
                      Hủy
                    </Button>
                  </Space>
                </div>
              }
              title="Xác nhận xóa"
              trigger="click"
              open={deleteConfirmVisible === record.uuid}
              onOpenChange={(visible) => setDeleteConfirmVisible(visible ? record.uuid : null)}
            >
              {/* Xóa */}
              <Button disabled={false} type="text" size="small" icon={<DeleteOutlined />} danger />
            </Popover>
          </Space>
        ),
      },
    ];

    return [...baseColumns, ...dynamicColumns, ...endColumns];
  }, [
    setUserProfileModal,
    deleteConfirmVisible,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleEditClick,
  ]);

  useEffect(() => {
    fetchCollectItems();
  }, [fetchCollectItems]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCollectItems();
    }
  }, [refreshTrigger, fetchCollectItems]);

  // Filter functions
  const handleApplyFilter = (filters: FilterOptions) => {
    setAppliedFilters(filters);
  };

  // Multiselect functions
  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    setBulkDeleteLoading(true);
    try {
      const response = await CollectItemAPIs.remote_bulkDelete({
        uuids: selectedRowKeys as string[],
      });
      message.success(`Đã xóa ${response.data.deleted_count} bản ghi thành công`);
      setSelectedRowKeys([]);
      await fetchCollectItems();
    } catch (error: any) {
      console.error('Error bulk deleting collect items:', error);
      message.error(error?.response?.data?.message || 'Lỗi khi xóa dữ liệu');
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [selectedRowKeys, fetchCollectItems]);

  const handleBulkDownload = useCallback(async (format: 'xlsx' | 'geojson' | 'csv' | 'shp') => {
    if (!collectFormUuid || selectedRowKeys.length === 0) return;

    const formatMap: Record<string, 'ESRI Shapefile' | 'MapInfo File' | 'LIBKML' | 'XLSX' | 'GeoJSON'> = {
      xlsx: 'XLSX',
      geojson: 'GeoJSON',
      csv: 'GeoJSON',
      shp: 'ESRI Shapefile',
    };

    const exportFormat = formatMap[format];
    const extensions: Record<string, string> = {
      'ESRI Shapefile': 'zip',
      'MapInfo File': 'zip',
      LIBKML: 'kml',
      XLSX: 'xlsx',
      GeoJSON: 'geojson',
    };

    try {
      const request: CollectItem_Export_RequestQuery = {
        form_uuid: collectFormUuid,
        format: exportFormat,
        uuids: selectedRowKeys as string[],
      };

      const response = await CollectItemAPIs.remote_exportCollectItems(request);

      const extension = extensions[exportFormat];
      const filename = `collect_items_selected_${Date.now()}.${extension}`;

      const downloadUrl = `${API_URL_ADMIN}${response.data.download_url}`;
      await StreamSaverUtils.download(downloadUrl, filename);

      message.success(`Đã tải xuống ${selectedRowKeys.length} bản ghi`);
    } catch (error: any) {
      console.error('Export error:', error);
      if (error?.response?.status === 404) {
        message.warning(
          error?.response?.data?.i18n?.vi || 'Không tìm thấy dữ liệu để xuất',
        );
      } else {
        message.error(error?.response?.data?.message || error?.response?.data?.i18n?.vi || 'Lỗi khi xuất dữ liệu');
      }
    }
  }, [collectFormUuid, selectedRowKeys]);

  const handleSelectAll = useCallback(() => {
    const allKeys = filteredCollectItems.map((item) => item.uuid);
    setSelectedRowKeys(allKeys);
  }, [filteredCollectItems]);

  const handleClearSelection = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  // Clear selection when form changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [collectFormUuid]);

  const rowSelection: TableProps<CollectItem_GetListWithUUIDs_ResponseItem>['rowSelection'] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  if (!selectedFormUUID) {
    return (
      <FormListPanel
        onMaximize={onMaximize}
        onClose={onClose}
        isMaximized={isMaximized}
      />
    );
  }

  return (
    <Container>
      <Toolbar
        isMaximized={isMaximized}
        loading={loading}
        collectItemsCount={collectItems.length}
        recordCount={paginationInfo.total}
        onOpenFilter={() => setFilterPopupVisible(true)}
        onOpenExport={() => setExportModalVisible(true)}
        onRefresh={fetchCollectItems}
        onMaximize={onMaximize}
        onClose={onClose}
      />

      {!isMobile && selectedRowKeys.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedRowKeys.length}
          totalCount={filteredCollectItems.length}
          onBulkDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onClearSelection={handleClearSelection}
          onSelectAll={handleSelectAll}
          loading={bulkDeleteLoading}
        />
      )}

      {isMobile ? (
        <MobileCardList
          data={mobileCardData}
          loading={loading}
          onItemClick={(item) => {
            const collectItem = collectItems.find((c) => c.uuid === item.id);
            if (collectItem) highlightOnMap(collectItem);
          }}
          onCreatorClick={(creatorId) => {
            setUserProfileModal({
              visible: true,
              userId: parseInt(creatorId),
            });
          }}
          onEditClick={(item) => {
            const collectItem = collectItems.find((c) => c.uuid === item.id);
            if (collectItem) handleEditClick(collectItem);
          }}
          onDeleteClick={(item) => {
            handleDeleteConfirm(item.id);
          }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
          formData={selectedFormData ?? undefined}
        />
      ) : (
        <Table
          dataSource={filteredCollectItems}
          columns={columns}
          rowKey={'uuid'}
          rowSelection={rowSelection}
          bordered
          style={{
            cursor: 'pointer',
            flex: 1,
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f1f5f9',
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                highlightOnMap(record);
              },
            };
          }}
          size="small"
          loading={loading}
          scroll={{ y: tableHeight ? `calc(${tableHeight} - 20px)` : undefined, x: '100%' }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            pageSizeOptions: [10, 20, 50, 100],
            position: ['bottomCenter'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total}`,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        collectFormUuid={selectedFormUUID}
        collectItemsCount={collectItems.length}
      />

      {/* Filter Popup */}
      <CollectItemFilterPopup
        visible={filterPopupVisible}
        onClose={() => setFilterPopupVisible(false)}
        onApplyFilter={handleApplyFilter}
        formData={selectedFormData ?? undefined}
      />
    </Container>
  );
};

export default memo(CollectDataTab);

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 4px;
  }

  .ant-table-thead > tr > th {
    font-size: 10px !important;
    padding: 4px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px !important;
  }

  .ant-table-measure-row td {
    padding: 0 !important;
  }
`;

const CreatorName = styled.span`
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;
